import React from 'react';
import ProposalCard from './ProposalCard';
import { formatTime, renderGoldMarks } from './helpers';

/**
 * Dispatch a single thread item to the right renderer.
 * `ctx` carries the editing flag + the proposal handlers (kept off the items
 * themselves so item objects stay JSON-serialisable).
 *
 * `ctx.typewriterProgress` (0..1) and `ctx.typewriterTargetId` together drive
 * the diary "writes itself" animation on the most recent coach reply.
 */
export function renderThreadItem(item, ctx) {
  switch (item.kind) {
    case 'user':
      return <UserNote key={item.id} body={item.body} t={item.t} />;
    case 'coach': {
      const isTypewriting = ctx && ctx.typewriterTargetId === item.id;
      const progress = isTypewriting ? (ctx.typewriterProgress ?? 1) : 1;
      return <CoachReply key={item.id} reply={item.reply} typewriterProgress={progress} />;
    }
    case 'proposal':
      return (
        <ProposalCard
          key={item.id}
          change={item.change}
          isEditing={ctx.editingId === item.change.change_id}
          onConfirm={ctx.onConfirm}
          onDelete={ctx.onDelete}
          onEditOpen={ctx.onEditOpen}
          onEditCancel={ctx.onEditCancel}
          onEditSave={ctx.onEditSave}
        />
      );
    case 'note':
      return <CoachNote key={item.id} text={item.text} />;
    default:
      return null;
  }
}

function UserNote({ body, t }) {
  return (
    <div className="ref-thread-item user-note fade-up-enter">
      <div className="ref-user-body">{body}</div>
      <div className="ref-user-time mono">{formatTime(t)}</div>
    </div>
  );
}

function CoachReply({ reply, typewriterProgress }) {
  const text = typeof reply === 'string' ? reply : (reply == null ? '' : String(reply));
  const p = typeof typewriterProgress === 'number' ? typewriterProgress : 1;

  if (p >= 1) {
    return (
      <div className="ref-thread-item coach-reply fade-up-enter">
        {renderGoldMarks(text)}
      </div>
    );
  }

  // Slice the raw string while typing — gold-mark formatting is applied once
  // the full text has landed (avoids partial {{tokens}} flashing on screen).
  const sliceLen = Math.ceil(text.length * Math.max(0, Math.min(1, p)));
  const visible = text.slice(0, sliceLen);
  return (
    <div className="ref-thread-item coach-reply fade-up-enter is-typing">
      <span>{visible}</span>
      <span className="ref-typewriter-cursor" aria-hidden="true" />
    </div>
  );
}

function CoachNote({ text }) {
  return (
    <div className="ref-thread-item coach-note fade-up-enter">{text}</div>
  );
}
